import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { getThreads, getTeacherRoster, startThread } from '../../services/teacher';
import { useTeacherMessaging } from '../../contexts/TeacherMessagingContext';

export default function TeacherInboxPage() {
  const navigate = useNavigate();
  const { refreshThreads } = useTeacherMessaging();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getThreads();
      setThreads(res.data?.results || res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDialog = async () => {
    setDialogOpen(true);
    setSelectedStudent(null);
    try {
      const res = await getTeacherRoster();
      setRoster(res.data?.results || res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleStart = async () => {
    if (!selectedStudent) return;
    setStarting(true);
    try {
      const res = await startThread({ student_id: selectedStudent.id });
      setDialogOpen(false);
      await refreshThreads();
      navigate(`/teacher/inbox/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Messages</Typography>
        <Button variant="contained" onClick={openDialog}>
          Message a Parent
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Button size="small" onClick={load} sx={{ mb: 2 }}>
        Refresh
      </Button>

      {threads.length === 0 ? (
        <Typography color="text.secondary">No conversations yet. Start one with a parent.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {threads.map((t) => (
            <Box
              key={t.id}
              onClick={() => navigate(`/teacher/inbox/${t.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/teacher/inbox/${t.id}`)}
              role="button"
              tabIndex={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: t.unread ? 'action.hover' : 'background.paper',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t.unread ? (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box sx={{ width: 10, flexShrink: 0 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: t.unread ? 700 : 500 }}>
                    {t.student_name}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({t.parent_name})
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {t.last_message_preview || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => !starting && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Message a parent</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={roster}
            getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
            value={selectedStudent}
            onChange={(_, v) => setSelectedStudent(v)}
            renderInput={(params) => <TextField {...params} label="Student" margin="normal" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={starting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStart} disabled={!selectedStudent || starting}>
            {starting ? 'Opening…' : 'Open thread'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
